import { V2_ENDPOINT } from "@/consts";
import { authenticatedFetch } from "@/lib/authenticatedFetch";
import { useCallback } from "react";
export interface TriggerTestData {
  success: boolean;
  result?: Record<string, unknown>;
  results?: Record<string, unknown>[];
  error?: {
    code: string;
    message: string;
  };
  executionTimeMs: number;
  triggerType: string;
  source: string;
}
interface TriggerTestApiResponse {
  code: number;
  message: string;
  data: TriggerTestData;
}

/**
 * Hook for testing triggers via the backend API.
 * Supports fetching live data from trigger sources and loading last run data.
 */
export function useTriggerTest() {
  const fetchTriggerData = useCallback(async (endpoint: string, source: "live" | "last_run", fetchOptions?: RequestInit): Promise<TriggerTestData | null> => {
    try {
      const response = await authenticatedFetch<TriggerTestApiResponse>(endpoint, fetchOptions);
      if (!response.ok) {
        let errorMessage = `Request failed with status ${response.status}`;
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorData.error?.message || errorMessage;
        } catch {
          // not JSON
        }
        console.error("Trigger test request failed:", errorMessage);
        return null;
      }
      const data = await response.json();
      const triggerData = data.data;
      if (!triggerData.success) {
        return source === "live" ? triggerData : null;
      }
      return triggerData;
    } catch (error) {
      console.error("Trigger test failed:", error);
      return null;
    }
  }, []);
  const fetchLiveData = useCallback(async (triggerType: string, payload: Record<string, unknown>): Promise<TriggerTestData | null> => fetchTriggerData(`${V2_ENDPOINT}/v1/triggers/${triggerType}/test`, "live", {
    method: "POST",
    headers: {
      "Content-Type": "application/json"
    },
    body: JSON.stringify({
      payload
    })
  }), [fetchTriggerData]);
  const fetchLastRunData = useCallback(async (workflowId: string): Promise<TriggerTestData | null> => fetchTriggerData(`${V2_ENDPOINT}/v1/workflows/${workflowId}/last-trigger-result`, "last_run"), [fetchTriggerData]);
  return {
    fetchLiveData,
    fetchLastRunData
  };
}